import { inject, Injectable } from '@angular/core';
import { CartovistaParameters } from '@app/core/model/entities/external-api/cartovista-parameters';
import { JMapParameters } from '@app/core/model/entities/external-api/jmap-parameters';
import { CartovistaParametersInput } from '@app/core/model/inputs/cartovista-parameters-input';
import { JmapParametersInput } from '@app/core/model/inputs/jmap-parameters-input';
import { RefreshResult } from '@app/core/model/other/refresh-result';
import ApiService from '@services/api.service';
import { AppManager } from '@services/managers/app.manager';
import { gql } from 'apollo-angular';
import { plainToInstance } from 'class-transformer';
import { finalize, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export enum AssetMapSource {
  NONE = 'NONE',
  TOO_MANY_RESULTS = 'TOO_MANY_RESULTS',
  JMAP = 'JMAP',
  CARTOVISTA = 'CARTOVISTA'
}

export interface CartovistaMap {
  id: string;
  title: string;
  vanityUrl: string;
}

@Injectable({providedIn: 'root'})
export class GisService {

  public state: 'none' | 'pending' = 'none';

  private apiService = inject(ApiService);
  private appManager: AppManager = inject(AppManager);

  /**
   * Fetches the organization's available GIS service. Only one service should be returned.
   */
  public availableGISService(): Observable<AssetMapSource> {
    const query = gql`
      query GisService($organizationId: String!) {
        gisService(organizationId: $organizationId)
      }
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables})
      .pipe(
        map(data => data['gisService'] as AssetMapSource)
      );
  }

  /**
   * Fetches the organization's Cartovista parameters.
   * @param organizationId The organization's ID
   * @return Observable that emits the Organization's Cartovista parameters.
   */
  public getCartovistaParameters(organizationId: string): Observable<CartovistaParameters> {
    const query = gql`
      query CartovistaParameters($organizationId: String!) {
        cartovistaParameters(organizationId: $organizationId) {
          id
          serverUrl
          tenantCode
          mapUrl
          apiKey
          areApiKeysSet
        }
      }
    `;

    const variables = {
      organizationId: organizationId
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(CartovistaParameters, data['cartovistaParameters'])));
  }

  /**
   * API call to fetch the organization's available maps
   * @return The list of available maps
   */
  public fetchAvailableCartovistaMaps(): Observable<CartovistaMap[]> {
    const query = gql`
      query AvailableMaps($organizationId: String!) {
        cartovistaAvailableMaps(organizationId: $organizationId) {
          id
          title
          vanityUrl
        }
      }
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => data['cartovistaAvailableMaps'] as CartovistaMap[]));
  }

  /**
   * API call to update the Organization's Cartovista parameters.
   * @param parametersInput the new Cartovista parameters
   * @return the updated parameters
   */
  public upsertOrganizationCartovistaParameters(
    parametersInput: CartovistaParametersInput
  ): Observable<CartovistaParameters> {
    const mutation = gql`
      mutation UpsertOrganizationCartovistaParameters($organizationId: String!, $parametersInput: CartovistaParametersInput!) {
        upsertOrganizationCartovistaParameters(organizationId: $organizationId, cartovistaParametersInput: $parametersInput) {
          id
          serverUrl
          tenantCode
          mapUrl
          areApiKeysSet
        }
      }
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      parametersInput: parametersInput
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(map(data => plainToInstance(CartovistaParameters, data['upsertOrganizationCartovistaParameters'])));
  }

  /**
   * API call to refresh the asset data in Cartovista.
   */
  public refreshCartovistaData(): Observable<RefreshResult> {
    this.state = 'pending';
    const query = gql`
      subscription RefreshCartovistaMapData($organizationId: String!) {
        refreshCartovistaData(organizationId: $organizationId) {
          status
          windowId
          failMessage
        }
      }
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id
    };
    return this.apiService.subscribe({query, variables})
      .pipe(
        map(data => data?.['refreshCartovistaData'] as RefreshResult),
        finalize(() => this.state = 'none')
      );
  }

  /**
   * Fetches the Organization's JMap parameters.
   * @param organizationId The organization's ID
   * @return Observable that emits the Organization's JMap parameters.
   */
  public getJMapParameters(organizationId: string): Observable<JMapParameters> {
    const query = gql`
      query JMapParameters($organizationId: String!) {
        jmapParameters(organizationId: $organizationId) {
          serverUrl
          projectId
          areCredentialsSet
        }
      }
    `;
    const variables = {
      organizationId: organizationId
    };
    return this.apiService.query({query, variables})
      .pipe(map(data => plainToInstance(JMapParameters, data['jmapParameters'])));
  }

  /**
   * API call to update the Organization's JMap parameters.
   * @param jmapParametersInput the new jmap parameters
   * @return the updated parameters
   */
  public upsertOrganizationJmapParameters(
    jmapParametersInput: JmapParametersInput
  ): Observable<JMapParameters> {
    const mutation = gql`
      mutation UpsertOrganizationJmapParameters($organizationId: String!, $jmapParametersInput: JmapParametersInput!) {
        upsertOrganizationJmapParameters(organizationId: $organizationId, jmapParametersInput: $jmapParametersInput) {
          serverUrl
          projectId
          areCredentialsSet
        }
      }
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id,
      JmapParametersInput: jmapParametersInput
    };

    return this.apiService.mutate({mutation, variables})
      .pipe(
        map(data => plainToInstance(JMapParameters, data['upsertOrganizationJmapParameters']))
      );
  }

  /**
   * API call to refresh the asset data in Jmap.
   */
  public refreshJmapData(): Observable<RefreshResult> {
    this.state = 'pending';
    const query = gql`
      subscription RefreshJmapMapData($organizationId: String!) {
        refreshJmapData(organizationId: $organizationId) {
          status
          windowId
          failMessage
        }
      }
    `;

    const variables = {
      organizationId: this.appManager.currentOrganization.id
    };
    return this.apiService.subscribe({query, variables})
      .pipe(
        map(data => data?.['refreshJmapData'] as RefreshResult),
        finalize(() => this.state = 'none')
      );
  }
}
